import { isMobile } from 'embed_v2/helpers/device'
import usePosition from 'embed_v2/hooks/usePosition'
import { render, ComponentChild } from 'preact'
import { CSSProperties } from 'preact/compat'
import { useState, useEffect, useRef } from 'preact/hooks'
import ErrorTooltip from './ErrorTooltip'
import OptionsSeparator from './OptionsSeparator'

const PLACEHOLDER = 'Select...'

const IndicatorIcon = () => (
  <svg
    height="20"
    width="20"
    viewBox="0 0 20 20"
    aria-hidden="true"
    focusable="false"
    className="wf_reset"
    fill="currentColor"
  >
    <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
  </svg>
)

const defaultOptionStyles = {
  cursor: 'default',
  display: 'block',
  fontSize: 'inherit',
  padding: '8px 12px',
  width: '100%',
  userSelect: 'none',
  boxSizing: 'border-box',
  transition: '90ms ease-in-out',
  transitionProperty: 'color, background-color'
}

const optionLabelStyles = {
  fontSize: 14,
  color: 'rgba(39,44,94,1)',
  lineHeight: 1.3,
  textTransform: 'none',
  letterSpacing: 'normal',
  textShadow: '0px 0px 0px rgb(0 0 0 / 0%)',
  fontFamily: 'Ubuntu',
  fontWeight: 'normal'
}

const EmptyOption = () => (
  <div style={defaultOptionStyles} role="option">
    <div className="wf_reset wf_datetime_dropdown_option_default" style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={optionLabelStyles} className="wf_reset">
        No Options
      </div>
    </div>
  </div>
)

export type OptionState = { isSelected: boolean; isFocused: boolean }
type OptionStylesType = (o: CSSProperties, { isSelected, isFocused }: OptionState) => CSSProperties

type OptionProps<T> = {
  option: T
  selected: T
  formatOptionLabel: (option: T & OptionState) => ComponentChild
  onChange: (option: T) => void
  optionStyles: OptionStylesType
}

const DropdownOption = <T extends Option>(props: OptionProps<T>) => {
  const { option, selected, formatOptionLabel } = props
  const { value, label } = option
  const isSelected = value === selected.value

  const [isFocused, setFocused] = useState(false)

  const onSelect = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    props.onChange(option)
  }

  const onMouseEnter = (e: MouseEvent) => {
    e.preventDefault()
    setFocused(true)
  }

  const onMouseLeave = (e: MouseEvent) => {
    e.preventDefault()
    setFocused(false)
  }

  const optionStyles = props.optionStyles(defaultOptionStyles, { isSelected, isFocused })

  return (
    <div
      key={value}
      style={optionStyles}
      onMouseDown={onSelect}
      onClick={onSelect}
      role="option"
      aria-selected={isSelected ? 'true' : 'false'}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {typeof formatOptionLabel === 'function' ? (
        formatOptionLabel({ ...option, isSelected, isFocused })
      ) : (
        <div
          className="wf_reset wf_datetime_dropdown_option_default"
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <div style={optionLabelStyles} className="wf_reset">
            {label}
          </div>
        </div>
      )}
    </div>
  )
}

export type State<T> = {
  selected: T
  isOpen: boolean
}

export type DropdownCustomStyles<T> = {
  menu: (o: CSSProperties, state: State<T>) => CSSProperties
  control: (o: CSSProperties, state: State<T>) => CSSProperties
  value: (o: CSSProperties, state: State<T>) => CSSProperties
  singleValue: (o: CSSProperties, state: State<T>) => CSSProperties
  indicator: (o: CSSProperties, state: State<T>) => CSSProperties
  placeholder: (o: CSSProperties) => CSSProperties
  option: OptionStylesType
  groupHeading?: () => CSSProperties
}

export type Option = {
  label: string
  value: string | number
  blank?: boolean // remove
  [key: string]: any
}

type SelectWithGroups<T> = {
  label: string
} & (
  | {
      options: readonly T[]
      value?: never
    }
  | {
      value: T
      options?: never
    }
)

type Props<T> = {
  includeBlank?: boolean
  options: T[] | SelectWithGroups<T>[]
  value?: T | null
  onChange: (option: T) => void
  onMenuOpen?: () => void
  placeholder?: string
  disabled?: boolean
  customStyles: DropdownCustomStyles<T>
  formatOptionLabel: (option: T & OptionState) => ComponentChild
  errorText?: string | null
  readOnly?: boolean
  groupBy?: (acc: object, sess: T) => object
}

const ReactDropdown = <T extends Option>(props: Props<T>) => {
  const { options } = props

  const mounted = useRef(true)

  const blankOption = {
    label: typeof props.placeholder === 'undefined' ? PLACEHOLDER : props.placeholder,
    value: '',
    blank: true
  } as T

  const [state, setState] = useState<State<T>>({
    selected: blankOption,
    isOpen: false
  })

  const { containerRef, popupRef } = usePosition([state.isOpen], { disableUpdate: isMobile })

  const closeMenu = () => {
    setState(prev => ({ ...prev, isOpen: false }))
  }

  const handleDocumentClick = (event: MouseEvent | TouchEvent) => {
    if (event.target instanceof HTMLElement) {
      if (mounted.current && event.target) {
        if (!containerRef.current?.contains(event.target)) {
          if (state.isOpen) {
            closeMenu()
          }
        }
      }
    }
  }

  useEffect(() => {
    if (props.value?.value !== state.selected.value) {
      const selectedOption = (() => {
        let res: T | undefined

        options.forEach(o => {
          if (o.options) {
            const oRes = o.options.find((oo: T) => oo.value === props.value?.value)
            if (oRes) {
              res = oRes as T
            }
          } else if (o.value === props.value?.value) {
            res = o as T
          }
        })

        return res
      })()

      const selected = props.value?.value ? selectedOption : blankOption

      if (selected) {
        setState(prev => ({ ...prev, selected }))
      }
    }
  }, [props.value])

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick, false)
    document.addEventListener('touchend', handleDocumentClick, false)

    return () => {
      mounted.current = false
      document.removeEventListener('click', handleDocumentClick, false)
      document.removeEventListener('touchend', handleDocumentClick, false)
    }
  }, [])

  const menuStyles = props.customStyles.menu(
    {
      position: 'fixed',
      zIndex: 2147483001,
      borderRadius: 4,
      boxShadow: '0 0 0 1px hsl(0deg 0% 0% / 10%), 0 4px 11px hsl(0deg 0% 0% / 10%)',
      maxHeight: 180,
      overflow: 'auto'
    },
    state
  )

  const onChange = (option: T) => {
    const newState = {
      selected: option,
      isOpen: false
    }
    if (newState.selected.value !== state.selected.value && props.onChange) {
      props.onChange(newState.selected)
    }
    setState(newState)
  }

  useEffect(() => {
    if (state.isOpen) {
      const dropdownWrapper = document.createElement('div')
      dropdownWrapper.classList.add('wf_reset')

      const el = document.body.appendChild(dropdownWrapper)

      const selectOptions = props.includeBlank ? [blankOption, ...options] : options

      const generateOption = (option: T) => (
        <DropdownOption
          key={option.value}
          option={option}
          selected={state.selected}
          onChange={onChange}
          optionStyles={props.customStyles.option}
          formatOptionLabel={props.formatOptionLabel}
        />
      )

      const generateOptions = () =>
        selectOptions.map((option, index) => {
          if (option.options) {
            return (
              <div key={index}>
                <OptionsSeparator label={option.label} optionStyles={props.customStyles.groupHeading?.()} />
                {option.options.map((suboption: Option) => generateOption(suboption as T))}
              </div>
            )
          }

          return generateOption(option as T)
        })

      render(
        <div style={menuStyles} className="wf_reset wf_skip_click_outside" aria-expanded="true" ref={popupRef}>
          {selectOptions.length ? generateOptions() : <EmptyOption />}
        </div>,
        el
      )

      return () => {
        el.remove()
      }
    }
  }, [state.isOpen])

  const handleMouseDown = (e: MouseEvent) => {
    if (props.readOnly) return
    if (e.button !== 0) return
    e.stopPropagation()
    e.preventDefault()

    if (!props.disabled) {
      props.onMenuOpen?.()
      setState(prev => ({ ...prev, isOpen: !state.isOpen }))
    }
  }

  const handleTouchEnd = (e: TouchEvent) => {
    if (props.readOnly) return
    e.stopPropagation()
    e.preventDefault()

    if (!props.disabled) {
      props.onMenuOpen?.()
      setState(prev => ({ ...prev, isOpen: !state.isOpen }))
    }
  }

  const controlStyles = props.customStyles.control(
    {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: '100%',
      minHeight: 50
    },
    state
  )

  const valueStyles = props.customStyles.value(
    {
      display: 'flex',
      flex: 1,
      alignItems: 'center',
      flexWrap: 'wrap',
      padding: '2px 8px',
      position: 'relative',
      overflow: 'hidden',
      boxSizing: 'border-box'
    },
    state
  )

  const singleValueStyles = props.customStyles.singleValue({}, state)

  const indicatorStyles = props.customStyles.indicator(
    {
      display: 'flex',
      padding: 8,
      boxSizing: 'border-box'
    },
    state
  )

  const dropdownStyles = {
    position: 'relative',
    boxSizing: 'border-box',
    height: '100%'
  }

  return (
    <div style={dropdownStyles} ref={containerRef} className="wf_reset wf_dropdown_container">
      <div
        style={controlStyles}
        className={`wf_reset wf_dropdown_control ${props.errorText ? 'wf_error' : ''}`}
        onMouseDown={handleMouseDown}
        onTouchEnd={handleTouchEnd}
        aria-haspopup="listbox"
      >
        <div style={valueStyles} className="wf_reset wf_dropdown_value">
          <div className="wf_reset wf_dropdown_value_container" style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={singleValueStyles} className="wf_reset wf_dropdown_value">
              {state.selected.label}
            </div>
          </div>
        </div>
        {!props.readOnly && (
          <div style={indicatorStyles} className="wf_reset wf_dropdown_indicator">
            <IndicatorIcon />
          </div>
        )}
      </div>
      {props.errorText ? <ErrorTooltip text={props.errorText} /> : null}
    </div>
  )
}

export default ReactDropdown
